import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FileField,
  FormActions,
  FormGrid,
  RequiredNote,
  SelectField,
  TextField,
  email,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../Components/Form';
import notify from '../../../utils/notify';
import useAwardingApi from '../hooks/useAwardingApi';
import { unwrapAwardingCollection } from '../utils/awardingHelpers';

const MAX_FILE_SIZE_MB = 10;

const SCHEMA = {
  file: { label: 'File', rules: [required()] },
  companyMitraId: { label: 'Registered supplier', rules: [required()] },
  picName: { label: 'PIC name', rules: [required()] },
  tradingEmail: {
    label: 'Trading email',
    rules: [required(), email('Enter a valid trading email address')],
  },
};
const FIELD_ORDER = ['file', 'companyMitraId', 'picName', 'tradingEmail'];

const UploadDealRecapModal = ({
  isOpen,
  toggle,
  onSubmit,
  isSubmitting,
  defaultValues = {},
}) => {
  const { getRegisteredSuppliers } = useAwardingApi();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [companyMitraId, setCompanyMitraId] = useState('');
  const [picName, setPicName] = useState('');
  const [tradingEmail, setTradingEmail] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    setFile(null);
    setCompanyMitraId(defaultValues.companyMitraId || '');
    setPicName(defaultValues.picName || '');
    setTradingEmail(defaultValues.tradingEmail || '');
    setErrors({});

    let cancelled = false;
    const loadSuppliers = async () => {
      setIsLoadingSuppliers(true);
      try {
        const result = await getRegisteredSuppliers();
        if (!cancelled) {
          setSuppliers(unwrapAwardingCollection(result));
        }
      } catch (error) {
        if (!cancelled) {
          notify.apiError(error, 'load registered supplier');
          setSuppliers([]);
        }
      } finally {
        if (!cancelled) setIsLoadingSuppliers(false);
      }
    };

    loadSuppliers();
    return () => {
      cancelled = true;
    };
    // defaultValues sengaja tidak masuk dependency agar form tidak ter-reset saat detail refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, getRegisteredSuppliers]);

  const fieldMode = isSubmitting ? 'disabled' : 'editable';

  const handleFileChange = event => {
    const selected = event.target.files?.[0];
    if (!selected) {
      setFile(null);
      return;
    }

    const isPdf =
      selected.type === 'application/pdf' ||
      selected.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      setErrors(prev => ({ ...prev, file: 'File must be a PDF' }));
      setFile(null);
      event.target.value = '';
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        file: `File size must not exceed ${MAX_FILE_SIZE_MB} MB`,
      }));
      setFile(null);
      event.target.value = '';
      return;
    }

    setErrors(prev => ({ ...prev, file: undefined }));
    setFile(selected);
  };

  const handleSupplierChange = pickedId => {
    const value = pickedId ?? '';
    setCompanyMitraId(value);
    setErrors(prev => ({ ...prev, companyMitraId: undefined }));

    const supplier = suppliers.find(item => item.id === value);
    if (supplier?.email && !tradingEmail) {
      setTradingEmail(supplier.email);
    }
  };

  const handleSubmit = () => {
    const nextErrors = validateForm(
      { file, companyMitraId, picName, tradingEmail },
      SCHEMA
    );
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, FIELD_ORDER);
      return;
    }

    return onSubmit({
      file,
      companyMitraId,
      picName: picName.trim(),
      tradingEmail: tradingEmail.trim(),
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalBody className='p-3 p-sm-4'>
        <div className='d-flex justify-content-between align-items-start mb-4'>
          <h5 className='fw-bold mb-0'>Upload Deal Recap</h5>
          <button
            type='button'
            className='btn-close'
            onClick={toggle}
            aria-label='Close'
            disabled={isSubmitting}
          ></button>
        </div>

        <RequiredNote />
        <FormGrid columns={1}>
          <FileField
            label='File'
            name='file'
            required
            accept='application/pdf,.pdf'
            inputRef={fileInputRef}
            hint={`PDF only, max ${MAX_FILE_SIZE_MB} MB`}
            error={errors.file}
            mode={fieldMode}
            onChange={handleFileChange}
          />
          <SelectField
            label='Send to'
            name='companyMitraId'
            required
            placeholder={
              isLoadingSuppliers
                ? 'Loading registered suppliers…'
                : 'Choose registered supplier'
            }
            options={suppliers.map(supplier => ({
              value: supplier.id,
              label: supplier.name,
            }))}
            value={companyMitraId}
            error={errors.companyMitraId}
            isLoading={isLoadingSuppliers}
            mode={isSubmitting || isLoadingSuppliers ? 'disabled' : 'editable'}
            onChange={handleSupplierChange}
          />
          <TextField
            label='PIC name'
            name='picName'
            required
            placeholder='e.g. Budi Santoso'
            value={picName}
            error={errors.picName}
            mode={fieldMode}
            onChange={e => {
              setPicName(e.target.value);
              setErrors(prev => ({ ...prev, picName: undefined }));
            }}
          />
          <TextField
            label='Trading email'
            name='tradingEmail'
            type='email'
            required
            placeholder='name@company.com'
            hint='The deal recap is sent to this address'
            value={tradingEmail}
            error={errors.tradingEmail}
            mode={fieldMode}
            onChange={e => {
              setTradingEmail(e.target.value);
              setErrors(prev => ({ ...prev, tradingEmail: undefined }));
            }}
          />
        </FormGrid>

        <FormActions>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={toggle}
            disabled={isSubmitting}
          >
            Close
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-upload-2-line'
            className='px-4'
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Upload & Sent File'}
          </ActionButton>
        </FormActions>
      </ModalBody>
    </Modal>
  );
};

export default UploadDealRecapModal;
